import { memo, useEffect, useRef, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { Clock3, GripVertical, Info, MapPin, UserPlus, Users } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import { getEffectiveResponse, getRemovalRule, schedulingRules } from '../schedulingRules'
import type { SelectionNoticeKind } from '../store/realtime-store'
import type { Job, Worker } from '../types'
import { getStaffing, getVisibleJobWorkers, isJobUnderstaffed } from '../utils/job-workers'
import type { JobDisplayStatus } from '../utils/job-status'
import { formatCompactDuration, formatDateKeyLabel, formatRelativeTime, formatTimeInZone, formatTimeRangeInZone, toDateKey } from '../utils/time'
import { AssignedWorkerRow } from './AssignedWorkerRow'
import { StatusChip, WorkerAvatar } from './status-meta'

export interface JobCardProps {
  job: Job
  display: JobDisplayStatus
  workersById: Record<string, Worker>
  nearbyCount: number
  isSelected: boolean
  /** Changes each time the job should flash (0 = no highlight). */
  highlightToken: number
  timeZone: string
  now: number
  canForceRemove: boolean
  /** Only passed to the selected card, so hover/flash changes re-render just that card. */
  hoveredWorkerId?: string | null
  flashedRows?: Record<string, number>
  /** Another admin removed / completed the selected job. */
  notice?: SelectionNoticeKind | null
  onDismissNotice?: () => void
  onToggle: (jobId: string) => void
  /** Select the job so the nearby-workers strip opens. */
  onAssign: (jobId: string) => void
  onRemoveWorker: (jobId: string, workerId: string) => void
  onHoverWorker: (workerId: string | null) => void
}

function describeTiming(job: Job, display: JobDisplayStatus, now: number): string | null {
  if (display.lateMinutes != null && display.lateMinutes > 0) return `${formatCompactDuration(display.lateMinutes)} late`
  if (display.kind === 'completed') return 'Done'
  if (display.kind === 'overdue') return 'Needs a worker'
  if (display.kind === 'in_progress') return `ends ${formatRelativeTime(job.scheduledEnd, now)}`
  return formatRelativeTime(job.scheduledStart, now)
}

/**
 * Scrolls only the jobs list (never the page), so selecting a job does not fight the page
 * scroll that brings the map into view on mobile.
 */
function revealInJobsList(element: HTMLElement) {
  const list = element.closest<HTMLElement>('[data-jobs-scroller]')
  if (!list) return
  const card = element.getBoundingClientRect()
  const view = list.getBoundingClientRect()
  const margin = 8
  let delta = 0
  if (card.top < view.top + margin) delta = card.top - view.top - margin
  else if (card.bottom > view.bottom - margin) delta = Math.min(card.bottom - view.bottom + margin, card.top - view.top - margin)
  if (delta === 0) return
  const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  list.scrollBy({ top: delta, behavior: smooth ? 'smooth' : 'auto' })
}

/**
 * Short address: the first line ("30 Parkway"); a bare house number is joined with the street
 * ("501, 501 Cathcart Rd" -> "501 Cathcart Rd"). Falls back to the area.
 */
function shortAddress(job: Job): string | null {
  const [first = '', second = ''] = (job.address ?? '').split(',').map((part) => part.trim()).filter(Boolean)
  if (/^\d+[a-z]?$/i.test(first) && second) return second.startsWith(first) ? second : `${first} ${second}`
  return first || job.area || null
}

/**
 * Compact job card. Selected = green border plus the essentials and one row per assigned worker
 * (Remove on each); it stays roughly the height of the unselected card. Adding workers happens
 * from the nearby-workers strip / map, not inside the card.
 */
export const JobCard = memo(function JobCard({
  job,
  display,
  workersById,
  nearbyCount,
  isSelected,
  highlightToken,
  timeZone,
  now,
  canForceRemove,
  hoveredWorkerId = null,
  flashedRows,
  notice = null,
  onDismissNotice,
  onToggle,
  onAssign,
  onRemoveWorker,
  onHoverWorker,
}: JobCardProps) {
  const understaffed = isJobUnderstaffed(job, now)
  const canDrag = understaffed && display.kind !== 'completed'
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `job:${job.id}`,
    data: { jobId: job.id },
    disabled: !canDrag,
  })
  const containerRef = useRef<HTMLDivElement | null>(null)
  const headerRef = useRef<HTMLButtonElement | null>(null)
  const [showAllWorkers, setShowAllWorkers] = useState(false)

  // On select: bring the card into view and move focus to it (unless focus is already inside).
  useEffect(() => {
    if (!isSelected) {
      setShowAllWorkers(false)
      return undefined
    }
    const frame = requestAnimationFrame(() => {
      if (containerRef.current) revealInJobsList(containerRef.current)
      if (!containerRef.current?.contains(document.activeElement)) headerRef.current?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(frame)
  }, [isSelected])

  const isOtherDay = display.kind === 'overdue'
  const when = isOtherDay
    ? `${formatDateKeyLabel(toDateKey(job.scheduledStart, timeZone))} · ${formatTimeInZone(job.scheduledStart, timeZone)}`
    : formatTimeRangeInZone(job.scheduledStart, job.scheduledEnd, timeZone)
  const timing = describeTiming(job, display, now)
  const timingIsAlert = display.kind === 'late' || display.kind === 'overdue' || (display.lateMinutes ?? 0) > 0
  const { assigned, required } = getStaffing(job, now)
  const visibleWorkers = getVisibleJobWorkers(job, now)
  const address = shortAddress(job)
  const maxRows = schedulingRules.maxVisibleWorkerRows
  const shownWorkers = showAllWorkers ? visibleWorkers : visibleWorkers.slice(0, maxRows)
  const hiddenCount = visibleWorkers.length - shownWorkers.length
  const workersId = `job-workers-${job.id}`
  const detached = notice === 'deleted'
  const removalRuleFor = (jobWorker: Job['workers'][number]) => (detached
    ? { allowed: false, requiresReason: false, blockedReason: 'Job no longer exists' }
    : getRemovalRule(job, jobWorker, { canForceRemove }))

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        containerRef.current = node
      }}
      role="listitem"
      className={cn(
        'relative rounded-lg border bg-white transition-shadow',
        isSelected ? 'border-[1.5px] border-[#169b62] shadow-sm' : 'border-[#dfeae3] hover:border-[#b9d9c7]',
        isDragging && 'opacity-40',
      )}
    >
      {highlightToken ? <span key={highlightToken} className="rt-flash pointer-events-none absolute inset-0 rounded-lg" aria-hidden="true" /> : null}

      <button
        ref={headerRef}
        type="button"
        onClick={() => onToggle(job.id)}
        aria-expanded={isSelected}
        aria-controls={isSelected ? workersId : undefined}
        aria-label={`${job.bookingNo}, ${job.service}, ${display.label}${timingIsAlert && timing ? `, ${timing}` : ''}, ${when}, ${assigned} of ${required} workers. ${isSelected ? 'Selected. Press to clear selection' : 'Select to show on the map'}`}
        className="block w-full rounded-lg px-2.5 pb-1.5 pt-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#169b62]"
      >
        <span className="flex items-center justify-between gap-1.5">
          <span className="truncate text-[12px] font-semibold text-[#20352b]">{job.bookingNo}</span>
          <StatusChip kind={display.kind} label={display.label} className="px-1.5 text-[10px]" />
        </span>
        <span className="mt-0.5 block truncate text-[12px] text-[#4e6157]">{job.service}</span>
        <span className="mt-1 flex items-center gap-1 text-[11px] text-[#718078]">
          <Clock3 className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate tabular-nums">
            {when}
            {timing ? <span className={cn('font-medium', timingIsAlert ? 'text-red-600' : 'text-[#4e6157]')}>{` · ${timing}`}</span> : null}
          </span>
        </span>
        {isSelected && address ? (
          <span className="mt-0.5 flex items-center gap-1 text-[11px] text-[#718078]" title={job.address ?? undefined}>
            <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{address}</span>
          </span>
        ) : null}
      </button>

      {isSelected && notice ? (
        <div role="status" className="mx-1.5 mb-1 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-1 text-[11px] text-amber-900">
          <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">{detached ? 'Removed or rescheduled by another admin.' : 'Marked completed by another admin.'}</span>
          <button type="button" onClick={onDismissNotice} className="shrink-0 font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
            {detached ? 'Close' : 'OK'}
          </button>
        </div>
      ) : null}

      {isSelected ? (
        <div id={workersId} className="border-t border-[#eef3ef] px-1.5 pb-1.5 pt-1">
          {job.staffingPending ? (
            <div className="flex items-center gap-1.5 px-1 py-1" aria-busy="true" aria-label={`Loading workers for ${job.bookingNo}`}>
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-3 flex-1" />
            </div>
          ) : visibleWorkers.length > 0 ? (
            <>
              <ul aria-label={`Workers on ${job.bookingNo}`} className="space-y-0.5">
                {shownWorkers.map((jobWorker) => {
                  const flashUntil = flashedRows?.[`${job.id}|${jobWorker.workerId}`]
                  return (
                    <AssignedWorkerRow
                      key={jobWorker.workerId}
                      variant="card"
                      job={job}
                      jobWorker={jobWorker}
                      worker={workersById[jobWorker.workerId] ?? null}
                      response={getEffectiveResponse(jobWorker, now)}
                      removal={removalRuleFor(jobWorker)}
                      now={now}
                      isHovered={hoveredWorkerId === jobWorker.workerId}
                      flashToken={flashUntil && flashUntil > Date.now() ? flashUntil : 0}
                      onHover={onHoverWorker}
                      onRemove={onRemoveWorker}
                    />
                  )
                })}
              </ul>
              {hiddenCount > 0 || showAllWorkers ? (
                <button
                  type="button"
                  onClick={() => setShowAllWorkers((value) => !value)}
                  aria-expanded={showAllWorkers}
                  className="ml-1 mt-0.5 text-[11px] font-semibold text-[#087a49] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]"
                >
                  {showAllWorkers ? 'Show less' : `+${hiddenCount} more`}
                </button>
              ) : null}
            </>
          ) : (
            <div className="px-1 py-0.5">
              <p className="text-[12px] font-medium text-[#20352b]">No worker assigned</p>
              {!detached && display.kind !== 'completed' ? <p className="text-[11px] text-[#718078]">Pick from nearby workers on the map</p> : null}
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-1.5 px-2.5 pb-2">
          {job.staffingPending ? (
            <span className="flex min-w-0 flex-1 items-center gap-1.5" aria-busy="true" aria-label={`Loading workers for ${job.bookingNo}`}>
              <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
              <Skeleton className="h-3 w-24" />
            </span>
          ) : visibleWorkers.length > 0 ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="flex -space-x-1.5">
                {visibleWorkers.slice(0, 3).map((jobWorker) => {
                  const worker = workersById[jobWorker.workerId]
                  return worker ? <WorkerAvatar key={worker.id} name={worker.name} avatarUrl={worker.avatarUrl} className="h-5 w-5 ring-2 ring-white" /> : null
                })}
              </span>
              <span className="truncate text-[11px] font-medium text-[#20352b]">
                {visibleWorkers.length === 1 ? workersById[visibleWorkers[0].workerId]?.name : `${visibleWorkers.length} workers`}
              </span>
              {required > 1 ? (
                <span className={cn('inline-flex shrink-0 items-center gap-0.5 text-[10px] font-semibold', understaffed ? 'text-amber-700' : 'text-[#718078]')}>
                  <Users className="h-3 w-3" aria-hidden="true" />
                  {assigned}/{required}
                </span>
              ) : null}
            </span>
          ) : display.kind === 'completed' ? (
            <span className="text-[11px] text-[#718078]">No worker recorded</span>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={() => onAssign(job.id)}
              className="h-6 gap-1 rounded-md bg-[#169b62] px-2 text-[11px] font-semibold hover:bg-[#118753]"
            >
              <UserPlus className="h-3 w-3" aria-hidden="true" />
              Assign{nearbyCount > 0 ? ` · ${nearbyCount} nearby` : ''}
            </Button>
          )}

          {canDrag ? (
            <button
              type="button"
              {...listeners}
              {...attributes}
              aria-label={`Drag ${job.bookingNo} onto a worker in the live schedule to add them`}
              className="flex h-6 w-6 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-[#718078] hover:bg-[#eef6f1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62] active:cursor-grabbing"
            >
              <GripVertical className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      )}
    </div>
  )
})
